import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DotLottiePlayer } from '@dotlottie/react-player';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import BackgroundPattern from '../components/BackgroundPattern';
import Footer from '../components/Footer';
import CustomerNavbar from '../components/CustomerNavbar';
import CaregiverNavbar from '../components/CaregiverNavbar';
import MobileFooter from '../components/MobileFooter';

const NotFound: React.FC = () => {
  usePageTitle('Page Not Found');
  const navigate = useNavigate();
  const { user } = useAuth();

  const PublicHeader = () => (
    <header className="bg-primary-600 shadow-lg relative z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl sm:text-3xl font-bold font-proyas tracking-wider text-white hover:text-gray-100 transition-colors">PROYAS</Link>
            <Link
              to="/"
              className="px-3 py-2 sm:px-4 sm:py-2 bg-white text-primary-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              Home
            </Link>
          </div>
        </div>
      </header>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col relative overflow-hidden">
      <BackgroundPattern />
      
      {/* Navbar Logic */}
      <div className="relative z-30">
        {user ? (
            user.userType === 'customer' ? <CustomerNavbar /> : <CaregiverNavbar />
        ) : (
            <PublicHeader />
        )}
      </div>

      <div className="flex-grow flex items-center justify-center px-4 py-12 relative z-10 pb-24 sm:pb-12">
        <div className="max-w-2xl w-full text-center">
          
            <div className="relative -mb-12 inline-block group">
                {/* Abstract shape background */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-white/40 rounded-full blur-3xl animate-pulse"></div>
                
                {/* Lottie Animation */}
                <div className="w-96 h-96 mx-auto relative z-10">
                    <DotLottiePlayer
                        src="https://lottie.host/0cc5c901-4d01-4702-85e3-10cf3d2b2056/11j6z2fcdr.lottie"
                        autoplay
                        loop
                        style={{ width: '100%', height: '100%' }}
                    />
                </div>
            </div>
          
            <h1 className="text-8xl font-black font-proyas text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-400 mb-2 drop-shadow-sm">
                404
            </h1>
            <h2 className="text-3xl font-bold text-gray-800 mb-4 tracking-tight">Lost in Space?</h2>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed max-w-md mx-auto">
                We couldn't find the page you're looking for. It might have been moved, deleted, or never existed.
            </p>

            <div className="flex justify-center items-center gap-3 sm:gap-4">
                <button
                onClick={() => navigate(-1)}
                className="px-6 sm:px-8 py-3 bg-white text-gray-700 border-2 border-gray-200 rounded-xl font-bold hover:bg-gray-50 hover:border-primary-200 hover:text-primary-700 transition-all shadow-sm"
                >
                Go Back
                </button>
                <button
                onClick={() => navigate(user ? `/${user.userType}/dashboard` : '/')}
                className="px-6 sm:px-8 py-3 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg hover:shadow-primary-600/30 transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                {user ? 'Dashboard' : 'Return Home'}
                </button>
            </div>
        </div>
      </div>
      
      {/* Footer Logic */}
      {user ? (
          <MobileFooter userType={user.userType} />
      ) : (
          <Footer />
      )}
    </div>
  );
};

export default NotFound;
